import { ArrowLeftIcon } from '@heroicons/react/24/solid';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ROUTES } from '../../../utils/routes';
import DismissibleModuleTutorialPopup from '../../common/Tutorials/DismissibleModuleTutorialPopup';

type ContentId = 'information' | 'appartement' | 'garage' | 'cave' | 'parking';

const Variable: React.FC<React.PropsWithChildren> = ({ children }) => (
  <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[0.925rem] font-semibold text-amber-900">{children}</span>
);

function BuildingSaleBoitage() {
  const navigate = useNavigate();
  const [active, setActive] = useState<ContentId>('information');
  const printRefs = useRef<Record<ContentId, HTMLDivElement | null>>({
    information: null, appartement: null, garage: null, cave: null, parking: null,
  });

  const handleGoBack = () => {
    try {
      if (window.history.state && window.history.state.idx > 0) {
        navigate(-1);
      } else {
        navigate(ROUTES.HOME);
      }
    } catch {
      navigate(ROUTES.HOME);
    }
  };

  const printContent = useCallback((id: ContentId) => {
    const node = printRefs.current[id];
    if (!node) { return; }
    const w = window.open('', '_blank');
    if (!w) { return; }
    const html = `<!doctype html>
    <html>
      <head>
        <meta charset="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Impression — Modèle ${id}</title>
        <style>
          html, body { font-family: Arial, sans-serif; line-height: 1.6; }
          .mail-preview { background: #f9fafb; border: 2px solid #e5e7eb; border-radius: 12px; padding: 20px; font-size: 15px; }
          .mail-title { font-weight: 700; font-size: 16px; }
          .variable { background: #fef3c7; color: #92400e; padding: 2px 6px; border-radius: 6px; font-weight: 600; }
          @media print { @page { margin: 15mm; } }
        </style>
      </head>
      <body>
        ${node.innerHTML}
        <script>window.onload = function(){ setTimeout(function(){ window.print(); window.close(); }, 100); }<\/script>
      </body>
    </html>`;
    w.document.open();
    w.document.write(html);
    w.document.close();
  }, []);

  const navItems = useMemo((): Array<{ id: ContentId; label: string; emoji: string; isSub?: boolean }>[] => ([
    [
      { id: 'information', label: 'INFORMATIONS JURIDIQUES', emoji: `<span role="img" aria-label="clipboard">📋</span>` },
    ],
    [
      { id: 'appartement', label: 'Appartement à vendre', emoji: `<span role="img" aria-label="office building">🏢</span>`, isSub: true },
      { id: 'garage', label: 'Garage à vendre', emoji: `<span role="img" aria-label="emoji">🚗</span>`, isSub: true },
      { id: 'cave', label: 'Cave à vendre', emoji: `<span role="img" aria-label="house">🏠</span>`, isSub: true },
      { id: 'parking', label: 'Parking à vendre', emoji: `<span role="img" aria-label="emoji">🅿</span>️`, isSub: true },
    ],
  ]), []);

  return (
    <div className="rounded-lg bg-white p-8 shadow-md">
      {/* Top actions */}
      <div className="mb-4 flex items-center justify-between">
        <button onClick={handleGoBack} className="flex items-center gap-2 text-rgpd-blue transition-colors hover:text-rgpd-blue-dark">
          <ArrowLeftIcon className="h-5 w-5" />
          <span>Retour</span>
        </button>
        <DismissibleModuleTutorialPopup tutorial="communication-building-sale-boitage" label="Tutoriel / Guide" />
      </div>

      {/* Header (uniform with Module 2) */}
      <div style={{
        background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 50%, #60a5fa 100%)',
        color: 'white',
        padding: '20px 30px',
        margin: '20px 20px 0 20px',
        borderRadius: '16px',
        boxShadow: '0 8px 25px rgba(30, 64, 175, 0.2)',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{ position: 'relative', zIndex: 1 }}>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 700, marginBottom: '8px', letterSpacing: '-0.02em', textShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <span role="img" aria-label="house">🏠</span> MODULE 5 - COMMUNICATION BIEN À VENDRE
          </h1>
          <div style={{ fontSize: '1rem', opacity: 0.9, fontWeight: 400, maxWidth: '800px', margin: '0 auto' }}>
            Informer les copropriétaires sur un bien à vendre dans leur immeuble
          </div>
        </div>
      </div>

      {/* Body (uniform with Module 2) */}
      <div style={{ display: 'flex', minHeight: 'calc(100vh - 200px)' }}>
        {/* Sidebar */}
        <div style={{
          width: '320px',
          background: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
          margin: '20px 0 20px 20px',
          border: '1px solid rgba(226, 232, 240, 0.5)',
          position: 'sticky',
          top: '20px',
          height: 'calc(100vh - 140px)',
          overflowY: 'auto',
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)',
            color: 'white',
            padding: '20px',
            borderRadius: '16px 16px 0 0',
            textAlign: 'center',
            fontWeight: 700,
            fontSize: '1.1rem',
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
          }}>
            <span role="img" aria-label="books">📚</span> SOMMAIRE
          </div>

          {/* Primary item */}
          {navItems[0].map((item) => (
            <div key={item.id}
              onClick={() => setActive(item.id)}
              style={{
                padding: '16px 20px',
                fontSize: '0.9rem',
                fontWeight: 700,
                color: active === item.id ? 'white' : '#16a34a',
                background: active === item.id ? 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)' : '#f8fafc',
                borderBottom: '1px solid #e2e8f0',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: 'none',
              }}
            >
              <span><span dangerouslySetInnerHTML={{ __html: item.emoji }} /> {item.label}</span>
              <span style={{ fontSize: '0.8rem' }} />
            </div>
          ))}

          {/* Sub header */}
          <div style={{
            padding: '12px 20px',
            background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
            color: '#1e40af',
            fontWeight: 700,
            fontSize: '0.8rem',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            borderBottom: '1px solid #cbd5e1',
            marginTop: '5px',
          }}>
            <span role="img" aria-label="document">📄</span> MODÈLES POUR BOITAGE
          </div>

          {/* Sub items */}
          <div style={{ background: '#f8fafc' }}>
            {navItems[1].map((item) => (
              <div key={item.id}
                onClick={() => setActive(item.id)}
                style={{
                  padding: '12px 20px 12px 40px',
                  fontSize: '0.85rem',
                  color: active === item.id ? '#15803d' : '#64748b',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  borderLeft: active === item.id ? '3px solid #16a34a' : '3px solid transparent',
                  background: active === item.id ? '#dcfce7' : 'transparent',
                  fontWeight: active === item.id ? 600 : 400,
                  border: 'none',
                  position: 'relative',
                }}
              >
                <span style={{ position: 'absolute', left: 20, color: '#cbd5e1' }}>└</span>
                <span dangerouslySetInnerHTML={{ __html: item.emoji }} /> {item.label}
              </div>
            ))}
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, padding: '20px', overflowY: 'auto' }}>
          {/* Information section */}
          {active === 'information' && (
            <section className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
              <div className="bg-blue-600 px-6 py-4 text-center text-sm font-bold tracking-wide text-white uppercase"><span role="img" aria-label="emoji">📢</span> MODÈLES D'INFORMATION AUX COPROPRIÉTAIRES VIA LE BOITAGE</div>
              <div className="space-y-6 p-6">
                <div className="rounded-xl border-l-4 border-green-600 bg-gradient-to-br from-slate-50 to-slate-100 p-6 shadow-sm">
                  <h3 className="mb-3 flex items-center gap-2 text-lg font-bold text-green-600">ℹ️ Résumé de la situation juridique</h3>
                  <p className="mb-3 text-justify"><strong>Le dilemme :</strong> Lorsque vous avez un mandat de vente dans une copropriété, informer les copropriétaires soulève des questions délicates. D'un côté, une communication trop proactive par email peut être perçue comme un détournement de finalité (risque : 5 ans de prison et 300 000€ d'amende). De l'autre côté, ne pas informer les copropriétaires peut générer des mécontentements car ils pourraient être intéressés pour eux-mêmes ou leurs proches.</p>
                  <div className="rounded-xl border-2 border-emerald-500 bg-gradient-to-r from-emerald-50 to-emerald-100 p-5 shadow-sm">
                    <h4 className="mb-2 font-bold text-emerald-700"><span role="img" aria-label="check mark">✅</span> SOLUTION RECOMMANDÉE : LE BOITAGE</h4>
                    <p className="text-emerald-700">Le boitage présente un risque RGPD réduit car il n'utilise pas de données nominatives. Les courriers ci-dessous sont conçus pour être <strong>généraux et anonymes</strong> (pas de nom de copropriétaire), tout en respectant les "stop pub".</p>
                  </div>
                  <p className="mt-3"><strong>Règles à respecter :</strong> Courrier général sans identification nominative • Respecter les affichages "stop pub" • Ton informatif et neutre • Une seule distribution par bien</p>
                </div>
              </div>
            </section>
          )}

          {/* Templates */}
          {active !== 'information' && (
            <section className="space-y-6">
              {/* Appartement */}
              {active === 'appartement' && (
                <div className="overflow-hidden rounded-2xl border-2 border-slate-200 shadow-sm" ref={(el) => { printRefs.current.appartement = el; }}>
                  <div className="flex min-h-[80px] items-center justify-between border-b-2 border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 px-5 py-4">
                    <div className="text-base font-bold text-slate-800"><span role="img" aria-label="office building">🏢</span> Courrier Appartement à Vendre (A UTILISER POUR LE BOÎTAGE)</div>
                    <div className="rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>Boîtage</div>
                  </div>
                  <div className="p-6">
                    <div className="mb-4">
                      <label className="mb-1 block font-semibold text-slate-700"><span role="img" aria-label="email">📧</span> Objet :</label>
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-800">Appartement à vendre dans votre copropriété</div>
                    </div>
                    <div className="mb-2" ref={(el) => { printRefs.current.appartement = el; }}>
                      <label className="mb-1 block font-semibold text-slate-700"><span role="img" aria-label="memo">📝</span> Corps du message :</label>
                      <div className="mail-preview">
                        Cher copropriétaire,<br /><br />
                        Nous vous informons qu'un appartement de votre résidence <Variable>[NOM_COPROPRIETE]</Variable> est actuellement proposé à la vente.<br /><br />
                        Cette information est communiquée de manière transparente, pensant que cette opportunité pourrait vous intéresser ou intéresser une personne de votre entourage.<br /><br />
                        <strong>Informations sur le bien :</strong><br />
                        • Type : <Variable>[TYPE_BIEN]</Variable><br />
                        • Surfaces et caractéristiques disponibles sur demande<br />
                        • Visite possible sur rendez-vous<br />
                        • Renseignements auprès du professionnel mandaté<br /><br />
                        Si cette opportunité vous intéresse ou si vous souhaitez plus d'informations, n'hésitez pas à nous contacter.<br /><br />
                        Cordialement,<br />
                        <Variable>[NOM_COMMERCIAL]</Variable><br />
                        Professionnel de l'immobilier<br />
                        <Variable>[COORDONNEES]</Variable>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-center">
                      <button className="rounded-md bg-rgpd-green px-4 py-2 text-white shadow" onClick={() => printContent('appartement')}><span role="img" aria-label="emoji">🖨</span>️ Imprimer le courrier</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Garage */}
              {active === 'garage' && (
                <div className="overflow-hidden rounded-2xl border-2 border-slate-200 shadow-sm" ref={(el) => { printRefs.current.garage = el; }}>
                  <div className="flex min-h-[80px] items-center justify-between border-b-2 border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 px-5 py-4">
                    <div className="text-base font-bold text-slate-800"><span role="img" aria-label="emoji">🚗</span> Courrier Garage à Vendre (A UTILISER POUR LE BOÎTAGE)</div>
                    <div className="rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>Boîtage</div>
                  </div>
                  <div className="p-6">
                    <div className="mb-4">
                      <label className="mb-1 block font-semibold text-slate-700"><span role="img" aria-label="email">📧</span> Objet :</label>
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-800">Garage à vendre dans votre copropriété</div>
                    </div>
                    <div className="mb-2" ref={(el) => { printRefs.current.garage = el; }}>
                      <label className="mb-1 block font-semibold text-slate-700"><span role="img" aria-label="memo">📝</span> Corps du message :</label>
                      <div className="mail-preview">
                        Cher copropriétaire,<br /><br />
                        Nous vous informons qu'un garage de votre copropriété <Variable>[NOM_COPROPRIETE]</Variable> est actuellement proposé à la vente.<br /><br />
                        Connaissant les difficultés de stationnement dans le secteur de <Variable>[QUARTIER]</Variable>, nous avons pensé que cette opportunité pourrait vous intéresser ou intéresser une personne de votre entourage.<br /><br />
                        <strong>Caractéristiques du garage :</strong><br />
                        • Localisation : <Variable>[LOCALISATION_GARAGE]</Variable><br />
                        • Accès facilité et sécurisé<br />
                        • Dimensions et spécificités disponibles sur demande<br />
                        • Visite possible sur rendez-vous<br /><br />
                        Cette place de stationnement représente un atout considérable pour la vie quotidienne et constitue également un investissement patrimonial intéressant.<br /><br />
                        Pour plus d'informations ou organiser une visite, nous restons à votre disposition.<br /><br />
                        Cordialement,<br />
                        <Variable>[NOM_COMMERCIAL]</Variable><br />
                        Professionnel de l'immobilier<br />
                        <Variable>[COORDONNEES]</Variable>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-center">
                      <button className="rounded-md bg-rgpd-green px-4 py-2 text-white shadow" onClick={() => printContent('garage')}><span role="img" aria-label="emoji">🖨</span>️ Imprimer le courrier</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Cave */}
              {active === 'cave' && (
                <div className="overflow-hidden rounded-2xl border-2 border-slate-200 shadow-sm" ref={(el) => { printRefs.current.cave = el; }}>
                  <div className="flex min-h-[80px] items-center justify-between border-b-2 border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 px-5 py-4">
                    <div className="text-base font-bold text-slate-800"><span role="img" aria-label="house">🏠</span> Courrier Cave à Vendre (A UTILISER POUR LE BOÎTAGE)</div>
                    <div className="rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>Boîtage</div>
                  </div>
                  <div className="p-6">
                    <div className="mb-4">
                      <label className="mb-1 block font-semibold text-slate-700"><span role="img" aria-label="email">📧</span> Objet :</label>
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-800">Cave à vendre dans votre copropriété</div>
                    </div>
                    <div className="mb-2" ref={(el) => { printRefs.current.cave = el; }}>
                      <label className="mb-1 block font-semibold text-slate-700"><span role="img" aria-label="memo">📝</span> Corps du message :</label>
                      <div className="mail-preview">
                        Cher copropriétaire,<br /><br />
                        Nous vous informons qu'une cave de votre copropriété <Variable>[NOM_COPROPRIETE]</Variable> est actuellement proposée à la vente.<br /><br />
                        Ces espaces de stockage étant de plus en plus rares et recherchés, nous avons pensé que cette opportunité pourrait vous intéresser ou intéresser une personne de votre connaissance.<br /><br />
                        <strong>Avantages de cette cave :</strong><br />
                        • Espace de stockage sécurisé<br />
                        • Accès pratique depuis l'immeuble<br />
                        • Complément idéal pour optimiser vos espaces de vie<br />
                        • Dimensions et localisation précise disponibles sur demande<br /><br />
                        Que ce soit pour vos besoins personnels de stockage ou comme investissement patrimonial, cette cave représente une opportunité intéressante.<br /><br />
                        Pour toute information complémentaire ou pour organiser une visite, nous restons à votre disposition.<br /><br />
                        Cordialement,<br />
                        <Variable>[NOM_COMMERCIAL]</Variable><br />
                        Professionnel de l'immobilier<br />
                        <Variable>[COORDONNEES]</Variable>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-center">
                      <button className="rounded-md bg-rgpd-green px-4 py-2 text-white shadow" onClick={() => printContent('cave')}><span role="img" aria-label="emoji">🖨</span>️ Imprimer le courrier</button>
                    </div>
                  </div>
                </div>
              )}

              {/* Parking */}
              {active === 'parking' && (
                <div className="overflow-hidden rounded-2xl border-2 border-slate-200 shadow-sm" ref={(el) => { printRefs.current.parking = el; }}>
                  <div className="flex min-h-[80px] items-center justify-between border-b-2 border-slate-200 bg-gradient-to-br from-slate-50 to-slate-100 px-5 py-4">
                    <div className="text-base font-bold text-slate-800"><span role="img" aria-label="emoji">🅿</span>️ Courrier Parking à Vendre (A UTILISER POUR LE BOÎTAGE)</div>
                    <div className="rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>Boîtage</div>
                  </div>
                  <div className="p-6">
                    <div className="mb-4">
                      <label className="mb-1 block font-semibold text-slate-700"><span role="img" aria-label="email">📧</span> Objet :</label>
                      <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-slate-800">Place de parking à vendre dans votre copropriété</div>
                    </div>
                    <div className="mb-2" ref={(el) => { printRefs.current.parking = el; }}>
                      <label className="mb-1 block font-semibold text-slate-700"><span role="img" aria-label="memo">📝</span> Corps du message :</label>
                      <div className="mail-preview">
                        Cher copropriétaire,<br /><br />
                        Nous vous informons qu'une place de parking de votre copropriété <Variable>[NOM_COPROPRIETE]</Variable> est actuellement proposée à la vente.<br /><br />
                        Dans le contexte actuel où le stationnement devient de plus en plus problématique, nous avons pensé que cette opportunité pourrait vous intéresser ou intéresser une personne de votre entourage.<br /><br />
                        <strong>Caractéristiques de cette place :</strong><br />
                        • Position <Variable>[POSITION_PARKING]</Variable><br />
                        • Accès sécurisé et facilité<br />
                        • Dimensions et spécificités disponibles sur demande<br />
                        • Facilité de manœuvre<br /><br />
                        Cette place de stationnement représente un atout majeur pour la vie quotidienne et constitue également un investissement patrimonial solide, que ce soit pour un usage personnel ou locatif.<br /><br />
                        Pour plus d'informations ou organiser une visite, nous restons à votre entière disposition.<br /><br />
                        Cordialement,<br />
                        <Variable>[NOM_COMMERCIAL]</Variable><br />
                        Professionnel de l'immobilier<br />
                        <Variable>[COORDONNEES]</Variable>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-center">
                      <button className="rounded-md bg-rgpd-green px-4 py-2 text-white shadow" onClick={() => printContent('parking')}><span role="img" aria-label="emoji">🖨</span>️ Imprimer le courrier</button>
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      </div>

      {/* local styles */}
      <style>{`
        .mail-preview { 
          background: linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%);
          border: 2px solid #e5e7eb; border-radius: 12px; padding: 20px; 
          font-size: 0.95rem; line-height: 1.7; box-shadow: inset 0 2px 4px rgba(0,0,0,0.04);
        }
        @keyframes slideInUp { to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}

export default BuildingSaleBoitage;


